import { useCallback, useEffect, useMemo, useState } from 'react';
import { useOutletContext, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { Plus, Cube, MagnifyingGlass, PencilSimple, TrashSimple, ArrowSquareOut, ArrowCounterClockwise, Barcode, FilePdf, Sparkle, ClockCounterClockwise, TrendUp, TrendDown, Warning, MapPin, DotsThree, Printer } from '@phosphor-icons/react';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { DataTable } from '@/components/ui/DataTable';
import { StorageThumb, StorageLink } from '@/components/StorageMedia';
import { engApi, ASSET_STATUS_LABEL, ASSET_STATUS_BADGE, CATEGORY_BADGE, CATEGORY_DOT, stockStatusOf } from './engApi';
import { EngAssetFormDialog } from './EngAssetFormDialog';
import AiAssetImportModal from '@/components/aiimport/AiAssetImportModal';
import { EngAssetActionDialog } from './EngAssetActionDialog';
import { ItemLifecycleActions } from '@/components/lifecycle/ItemLifecycleActions';
import LabelPrintDialog from '@/components/labels/LabelPrintDialog';
import { buildEngAssetLabel } from '@/components/labels/labelSizes';
import LinkedDocsPanel from '@/pages/docs/LinkedDocsPanel';
import TraceabilityPanel from '@/pages/trace/TraceabilityPanel';
import PurchaseSummaryCard from '@/components/PurchaseSummaryCard';

// 履歴に出る動きの日本語ラベル（§8.5 の操作 ＋ 自社製作の入庫）。
// 英語モードでは翻訳レイヤーが訳すので、ここは日本語で持つ。
const MV_LABEL = {
  use: '使用', scrap: 'スクラップ', return: '返却',
  receive: '入庫', receive_cancel: '入庫取消',
  issue: '出庫', move: '移動', install: '組込',
  repair: '修理', dispose: '廃棄', ship: '出荷', lend: '貸出',
};
const MV_COLOR = {
  use: 'text-sky-600', scrap: 'text-rose-600', return: 'text-emerald-600',
  receive: 'text-emerald-600', receive_cancel: 'text-zinc-500',
  issue: 'text-sky-600', move: 'text-indigo-600', install: 'text-sky-700',
  repair: 'text-amber-600', dispose: 'text-rose-600', ship: 'text-violet-600',
  lend: 'text-teal-600',
};

// 現在このプロジェクトで使われている（返却されていない）最新の使用先
function currentProject(r) {
  const uses = (r?.movements || []).filter((m) => m.type === 'use' && m.project_name);
  return uses.length ? uses[uses.length - 1].project_name : null;
}

function CategoryBadge({ name, color, testid }) {
  if (!name) {
    return <span className="inline-flex items-center rounded-full border border-rose-200 bg-rose-50 px-2 py-0.5 text-[11px] font-semibold text-rose-600" data-testid={testid}>未分類</span>;
  }
  return (
    <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-semibold ${CATEGORY_BADGE[color] || CATEGORY_BADGE.gray}`} data-testid={testid}>
      <span className={`h-1.5 w-1.5 rounded-full ${CATEGORY_DOT[color] || CATEGORY_DOT.gray}`} />
      {name}
    </span>
  );
}

function LocationCell({ row }) {
  if (row.shelf_id && row.location_path) {
    return <span className="text-[12px] text-zinc-700">{row.location_path}</span>;
  }
  return (
    <span className="inline-flex items-center gap-1.5">
      <span className="rounded border border-rose-200 bg-rose-50 px-1.5 py-0.5 text-[10px] font-bold text-rose-600">未設定</span>
      {row.location_path && <span className="text-[11px] text-zinc-400">{row.location_path}</span>}
    </span>
  );
}

export default function EngAssets() {
  const { access } = useOutletContext() || {};
  const isMember = !!access?.is_member;
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [catFilter, setCatFilter] = useState('all');
  const [cats, setCats] = useState([]);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [detail, setDetail] = useState(null);
  const [labelAsset, setLabelAsset] = useState(null);
  // 一括ラベル印刷 — IT資産(AssetManagement.openBulkLabelPrint)と同じ流れ:
  // 選択行 → buildEngAssetLabel の配列 → 既存の LabelPrintDialog。別経路は作らない。
  const [labelBatch, setLabelBatch] = useState(null);
  const [selected, setSelected] = useState(() => new Set());
  const [action, setAction] = useState(null); // {mode, asset}
  const [priceHistory, setPriceHistory] = useState(null); // asset for 価格履歴 dialog
  const [searchParams, setSearchParams] = useSearchParams();

  // QRスキャン → ?open={id} で詳細を直接開く
  useEffect(() => {
    const openId = searchParams.get('open');
    if (openId) {
      engApi.asset(openId).then(setDetail).catch(() => {});
      setSearchParams({}, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const load = useCallback(() => {
    setLoading(true);
    engApi.assets()
      .then(setRows)
      .catch(() => toast.error('資産の取得に失敗しました')).finally(() => setLoading(false));
  }, []);
  const [aiImportOpen, setAiImportOpen] = useState(false);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    // タブはカテゴリマスタから作る（返ってきた行から生やすのはやめる）。
    // 他台帳（設備台帳）のカテゴリは owner_ledger を見て除外 — 判定の正本はサーバ側
    engApi.assetCategories()
      .then((c) => setCats((c || []).filter((x) => !x.foreign_ledger)))
      .catch(() => {});
  }, []);

  const filtered = useMemo(() => {
    const nq = q.trim().toLowerCase();
    return rows.filter((r) => {
      if (statusFilter !== 'all' && stockStatusOf(r) !== statusFilter) return false;
      if (catFilter === '__none__') { if (r.asset_category_id) return false; }
      else if (catFilter !== 'all' && (r.asset_category_id || '') !== catFilter) return false;
      if (!nq) return true;
      return `${r.asset_no || ''} ${r.name || ''} ${r.sku || ''} ${r.drawing_no || ''} ${r.maker || ''} ${r.maker_pn || ''} ${r.model_no || ''} ${r.material || ''} ${r.location_path || ''} ${r.asset_category_name || ''} ${r.source_request_no || ''}`.toLowerCase().includes(nq);
    });
  }, [rows, q, statusFilter, catFilter]);

  // 在庫の見え方は実数量から導出する（保存値 status とのズレを表示に出さない）
  const counts = useMemo(() => ({
    all: rows.length,
    in_stock: rows.filter((r) => stockStatusOf(r) === 'in_stock').length,
    out_of_stock: rows.filter((r) => stockStatusOf(r) === 'out_of_stock').length,
    reserved: rows.filter((r) => stockStatusOf(r) === 'reserved').length,
    in_use: rows.filter((r) => stockStatusOf(r) === 'in_use').length,
    scrapped: rows.filter((r) => stockStatusOf(r) === 'scrapped').length,
  }), [rows]);

  const catCounts = useMemo(() => {
    const m = {};
    rows.forEach((r) => {
      const k = r.asset_category_id || '__none__';
      m[k] = (m[k] || 0) + 1;
    });
    return m;
  }, [rows]);

  const missingLocation = useMemo(() => rows.filter((r) => !r.shelf_id).length, [rows]);

  const onChanged = (fresh) => {
    setRows((prev) => prev.map((r) => (r.id === fresh.id ? fresh : r)));
    setDetail((d) => (d && d.id === fresh.id ? fresh : d));
  };

  const remove = async (r) => {
    if (!window.confirm(`「${r.name}」（${r.sku || r.asset_no}）を削除しますか？`)) return;
    try {
      await engApi.deleteAsset(r.id);
      toast.success('削除しました');
      setDetail(null);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || '削除に失敗しました'); }
  };

  return (
    <div className="space-y-5" data-testid="eng-assets-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-zinc-900">技術資産</h1>
          <p className="text-sm text-zinc-500 mt-1">SKU・品名・型式・現在地から資産を探せます</p>
        </div>
        {isMember && (
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={() => { setEditing(null); setFormOpen(true); }}
              className="h-10 border-zinc-200" data-testid="eng-new-asset-btn">
              <Plus size={16} weight="bold" className="mr-1.5" /> 資産を登録
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="h-10 w-10 p-0 text-zinc-500" data-testid="eng-assets-more-menu">
                  <DotsThree size={18} weight="bold" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <DropdownMenuItem onClick={() => setAiImportOpen(true)} data-testid="eng-ai-import-btn">
                  <Sparkle size={14} className="mr-2 text-zinc-500" /> AIインポート
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        )}
      </div>

      {/* 主アクション: 探す */}
      <div className="relative" data-testid="eng-search-hero">
        <MagnifyingGlass size={19} className="absolute left-4 top-1/2 -translate-y-1/2 text-zinc-400" />
        <Input value={q} onChange={(e) => setQ(e.target.value)}
          placeholder="資産を探す — SKU・品番・品名・型式・現在地・REQ番号"
          className="h-14 pl-12 text-base rounded-lg border-zinc-200 bg-white shadow-sm focus-visible:ring-2 focus-visible:ring-teal-500/30 focus-visible:border-teal-400"
          data-testid="eng-search" />
      </div>


      {missingLocation > 0 && (
        <div className="flex items-center gap-2 rounded-xl border border-amber-200 bg-amber-50 px-4 py-2.5" data-testid="eng-location-warning">
          <Warning size={18} weight="fill" className="text-amber-500 shrink-0" />
          <p className="text-[13px] text-amber-800">
            <span className="font-bold">{missingLocation}件</span> の資産に現在地が設定されていません。編集画面から現在地を設定してください。
          </p>
        </div>
      )}

      {/* カテゴリタブ — 業務上の意味で在庫を見る（品番プレフィックスではない） */}
      <div className="flex flex-wrap items-center gap-1.5" data-testid="eng-category-tabs">
        <button onClick={() => setCatFilter('all')} data-testid="eng-cat-filter-all"
          className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-colors ${
            catFilter === 'all' ? 'bg-zinc-900 text-white' : 'bg-white border border-zinc-200 text-zinc-500 hover:bg-zinc-50'
          }`}>
          すべて（{counts.all}）
        </button>
        {cats.map((c) => (
          <button key={c.id} onClick={() => setCatFilter(c.id)} data-testid={`eng-cat-filter-${c.id}`}
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${
              catFilter === c.id ? 'bg-zinc-900 text-white border-zinc-900 shadow' : `${CATEGORY_BADGE[c.color] || CATEGORY_BADGE.gray} hover:brightness-95`
            }`}>
            <span className={`h-1.5 w-1.5 rounded-full ${catFilter === c.id ? 'bg-white' : (CATEGORY_DOT[c.color] || CATEGORY_DOT.gray)}`} />
            {c.name_ja}（{catCounts[c.id] || 0}）
          </button>
        ))}
        {catCounts.__none__ > 0 && (
          <button onClick={() => setCatFilter('__none__')} data-testid="eng-cat-filter-none"
            className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${
              catFilter === '__none__' ? 'bg-zinc-900 text-white border-zinc-900' : 'bg-rose-50 border-rose-200 text-rose-600'
            }`}>
            未分類（{catCounts.__none__}）
          </button>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-1.5" data-testid="eng-stats-row">
        {[['all', '総資産'], ['in_stock', ASSET_STATUS_LABEL.in_stock], ['out_of_stock', ASSET_STATUS_LABEL.out_of_stock],
          ...(counts.reserved ? [['reserved', ASSET_STATUS_LABEL.reserved]] : []),
          ['in_use', ASSET_STATUS_LABEL.in_use], ['scrapped', ASSET_STATUS_LABEL.scrapped]].map(([s, label]) => (
          <button key={s} onClick={() => setStatusFilter(s)} data-testid={`eng-filter-${s}`}
            className={`px-3 py-1.5 rounded-full text-xs transition-colors border ${
              statusFilter === s ? 'bg-zinc-900 text-white border-zinc-900' : 'bg-white border-zinc-200 text-zinc-500 hover:bg-zinc-50'
            }`}>
            <span data-testid={`eng-stat-${s}`}>{label} <b className="tabular-nums">{counts[s]}</b></span>
          </button>
        ))}
      </div>

      {selected.size > 0 && (
        <div className="bg-teal-50 border-2 border-teal-200 rounded-xl px-4 py-3 flex items-center gap-3 flex-wrap shadow-sm sticky top-2 z-10" data-testid="eng-asset-bulk-bar">
          <span className="text-sm font-semibold text-teal-900">選択中 {selected.size}件</span>
          <div className="flex-1" />
          <Button size="sm" variant="outline" onClick={() => setSelected(new Set())} data-testid="eng-asset-bulk-clear" className="h-9">選択解除</Button>
          <Button size="sm" data-testid="eng-asset-bulk-label-btn" className="h-9 bg-teal-700 hover:bg-teal-800"
            onClick={() => setLabelBatch(filtered.filter((r) => selected.has(r.id)).map(buildEngAssetLabel))}>
            <Printer size={15} className="mr-1.5" /> ラベル一括印刷（{selected.size}）
          </Button>
        </div>
      )}

      {loading ? (
        <div className="py-16 text-center text-sm text-zinc-400" data-testid="eng-loading">読込中…</div>
      ) : filtered.length === 0 ? (
        <div className="bg-white rounded-xl border border-dashed border-zinc-300 p-10 text-center" data-testid="eng-empty">
          <Cube size={40} weight="duotone" className="text-zinc-300 mx-auto mb-2" />
          <p className="text-sm text-zinc-500">資産がありません</p>
          <p className="text-xs text-zinc-400 mt-1">技術分類の部品が入荷すると自動で登録されます</p>
        </div>
      ) : (
        <DataTable
          dataTestId="eng-asset-table"
          rows={filtered}
          minWidth="min-w-[1050px]"
          rowKey={(r) => r.id}
          rowTestId={(r) => `eng-asset-row-${r.id}`}
          onRowClick={(r) => setDetail(r)}
          columns={[
            // 幅の予算: 固定幅の合計776px + 品名(flex)270px = 1046px。型式を足しても
            // 品名を270pxから削らないため、上の minWidth で横スクロールさせる。
            { key: 'select',
              header: (
                <input type="checkbox" data-testid="eng-asset-select-all"
                  checked={filtered.length > 0 && filtered.every((r) => selected.has(r.id))}
                  ref={(el) => { if (el) { const some = filtered.some((r) => selected.has(r.id)); el.indeterminate = some && !filtered.every((r) => selected.has(r.id)); } }}
                  onChange={(e) => setSelected(e.target.checked ? new Set(filtered.map((r) => r.id)) : new Set())}
                  onClick={(e) => e.stopPropagation()}
                  className="h-4 w-4 rounded border-zinc-300 accent-teal-600 cursor-pointer align-middle"
                  title="全て選択 / 解除" />
              ),
              width: 'w-10', sortable: false, mobileBadge: true,
              cell: (r) => (
                <input type="checkbox" data-testid={`eng-asset-select-${r.id}`}
                  checked={selected.has(r.id)}
                  onChange={(e) => setSelected((prev) => { const n = new Set(prev); if (e.target.checked) n.add(r.id); else n.delete(r.id); return n; })}
                  onClick={(e) => e.stopPropagation()}
                  className="h-4 w-4 rounded border-zinc-300 accent-teal-600 cursor-pointer" />
              ) },
            { key: 'asset_category_name', header: 'カテゴリ', width: 'w-24', mobileBadge: true,
              cell: (r) => <CategoryBadge name={r.asset_category_name} color={r.asset_category_color} testid={`eng-asset-cat-${r.id}`} /> },
            { key: 'sku', header: 'SKU', width: 'w-28', primary: true, mono: true,
              cell: (r) => (
                <span className="font-mono text-teal-700 font-semibold">{r.sku || <span className="text-zinc-300">—</span>}</span>
              ) },
            // 内容の列。flex で固定幅列の残りを取り、2行で打ち切って全文は title に。
            { key: 'name', header: '品名', flex: true,
              cell: (r) => (
                <div className="min-w-0">
                  <span className="font-medium text-zinc-900 line-clamp-2 break-words" title={r.name}>{r.name}</span>
                  {currentProject(r) && <span className="block text-[11px] text-sky-600 truncate" title={currentProject(r)}>{currentProject(r)}</span>}
                </div>
              ) },
            // 型式・仕様。近い品名の判別用。値は短い仕様文字列が普通なので固定幅。
            { key: 'model_no', header: '型式', width: 'w-28',
              cell: (r) => (r.model_no
                ? <span className="line-clamp-2 break-words text-zinc-600 text-xs" title={r.model_no}>{r.model_no}</span>
                : <span className="text-zinc-300">—</span>) },
            { key: 'location_path', header: '現在地', width: 'w-28',
              cell: (r) => <LocationCell row={r} /> },
            { key: 'qty_free', header: '利用可', width: 'w-16', align: 'right', mobileBadge: true,
              cell: (r) => <span className="tabular-nums font-semibold text-zinc-900">{r.qty_free ?? r.qty_available} {r.uom || '個'}</span> },
            { key: 'qty_reserved', header: '予約中', width: 'w-16', align: 'right', mobileHidden: true,
              cell: (r) => (r.qty_reserved ? <span className="tabular-nums text-amber-600 font-semibold">{r.qty_reserved}</span> : <span className="text-zinc-300">—</span>) },
            { key: 'qty_used', header: '使用中', width: 'w-16', align: 'right', mobileHidden: true,
              cell: (r) => (r.qty_used ? <span className="tabular-nums text-sky-600 font-semibold">{r.qty_used}</span> : <span className="text-zinc-300">—</span>) },
            { key: 'status', header: '状態', width: 'w-28', mobileBadge: true, sortable: false,
              cell: (r) => (
                <Badge variant="outline" className={`${ASSET_STATUS_BADGE[stockStatusOf(r)]} text-[11px]`}
                  data-testid={`eng-asset-stock-${r.id}`}>
                  {ASSET_STATUS_LABEL[stockStatusOf(r)]}
                </Badge>
              ) },
          ]}
          empty={{ text: '資産がありません' }}
        />
      )}

      {/* detail dialog */}
      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto" aria-describedby={undefined} data-testid="eng-asset-detail">
          {detail && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2 flex-wrap">
                  <CategoryBadge name={detail.asset_category_name} color={detail.asset_category_color} testid="eng-detail-category" />
                  <span className="font-mono text-teal-700 text-sm">{detail.sku || detail.asset_no}</span>
                  {detail.name}
                  <Badge variant="outline" className={`${ASSET_STATUS_BADGE[stockStatusOf(detail)]} text-[11px]`}
                    data-testid="eng-asset-detail-stock">
                    {ASSET_STATUS_LABEL[stockStatusOf(detail)]}
                  </Badge>
                </DialogTitle>
              </DialogHeader>

              {/* §8.5 ひとつのライフサイクル — 全台帳で同じ操作 */}
              <ItemLifecycleActions register="eng" itemId={detail.id}
                onChanged={() => load()} testId="eng-asset-lifecycle"
                extraActions={[
                  ...(isMember && (detail.allowed_ops || ['use', 'return', 'scrap']).includes('return')
                    ? [{
                        key: 'return',
                        label: '返却',
                        icon: <ArrowCounterClockwise size={13} className="mr-1" weight="bold" />,
                        onClick: () => setAction({ mode: 'return', asset: detail }),
                        disabled: (detail.qty_used || 0) <= 0,
                        className: 'border-emerald-200 text-emerald-700 hover:bg-emerald-50',
                        testId: 'eng-detail-return',
                      }]
                    : []),
                  // 数量を持つ品（CSM など）だけ。個体管理の設備（EQP）には出さない。
                  ...(isMember && !String(detail.asset_no || '').toUpperCase().startsWith('EQP')
                    ? [{
                        key: 'receive',
                        label: '入庫',
                        onClick: () => setAction({ mode: 'receive', asset: detail }),
                        className: 'border-teal-200 text-teal-700 hover:bg-teal-50',
                        testId: 'eng-detail-receive',
                      }, {
                        key: 'adjust',
                        label: '在庫調整',
                        onClick: () => setAction({ mode: 'adjust', asset: detail }),
                        className: 'border-amber-200 text-amber-700 hover:bg-amber-50',
                        testId: 'eng-detail-adjust',
                      }]
                    : []),
                ]} />

              {/* 3秒で分かるサマリー — 何か / どこか / 誰が / どのプロジェクト */}
              <div className="rounded-xl border border-zinc-200 bg-zinc-50/70 p-3 grid grid-cols-2 gap-x-4 gap-y-2.5" data-testid="eng-detail-summary">
                <div>
                  <p className="text-[10px] font-bold uppercase tracking-wider text-zinc-400">現在地</p>
                  <p className="mt-0.5 flex items-center gap-1.5 text-sm font-semibold text-zinc-900" data-testid="eng-detail-location">
                    <MapPin size={14} weight="duotone" className="text-teal-600 shrink-0" />
                    <LocationCell row={detail} />
                  </p>
                </div>
                <div>
                  <p className="text-[10px] font-bold uppercase tracking-wider text-zinc-400">プロジェクト</p>
                  <p className="mt-0.5 text-sm font-semibold text-zinc-900" data-testid="eng-detail-project">
                    {detail.current_project_name || currentProject(detail)
                      || <span className="text-zinc-400 font-normal">未設定</span>}
                  </p>
                </div>
                <div>
                  <p className="text-[10px] font-bold uppercase tracking-wider text-zinc-400">保有者</p>
                  <p className="mt-0.5 text-sm font-semibold text-zinc-900" data-testid="eng-detail-holder">
                    {detail.current_holder_name || <span className="text-zinc-400 font-normal">未設定</span>}
                  </p>
                </div>
                <div>
                  <p className="text-[10px] font-bold uppercase tracking-wider text-zinc-400">利用可 / 予約中 / 使用中</p>
                  <p className="mt-0.5 text-sm font-semibold tabular-nums text-zinc-900" data-testid="eng-detail-qty">
                    {detail.qty_free ?? detail.qty_available} / <span className="text-amber-600">{detail.qty_reserved || 0}</span> / <span className="text-sky-600">{detail.qty_used || 0}</span> {detail.uom || '個'}
                  </p>
                </div>
                <div>
                  <p className="text-[10px] font-bold uppercase tracking-wider text-zinc-400">単価</p>
                  <div className="mt-0.5 text-sm font-semibold text-zinc-900">{renderUnitPrice(detail)}</div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
                <Field label="品番グループ（採番）" value={detail.sku_group_id} mono />
                <Field label="図番" value={detail.drawing_no} mono />
                <Field label="メーカー" value={detail.maker} />
                <Field label="メーカー品番" value={detail.maker_pn} mono />
                <Field label="型式・仕様" value={detail.model_no} />
                <Field label="材質" value={detail.material} />
                <Field label="分類" value={detail.category_name} />
                <Field label="在庫 / 総数" value={`${detail.qty_available} / ${detail.qty_total} ${detail.uom || '個'}`} />
                <Field label="使用中 / スクラップ" value={`${detail.qty_used || 0} / ${detail.qty_scrapped || 0}`} />
                <Field label="最新購入単価" value={detail.last_purchase_price != null ? `¥${Number(detail.last_purchase_price).toLocaleString()}${detail.last_purchase_date ? `（${detail.last_purchase_date}）` : ''}` : null} />
                <Field label="登録元" value={detail.source === 'parts_request' ? `部品受注 ${detail.source_request_no || ''}` : detail.source === 'development_part' ? '開発案件から昇格' : detail.source === 'fabrication' ? `自社製作 ${detail.source_fab_no || ''}`.trim() : '手動登録'} />
                <Field label="登録者" value={detail.created_by_name} />
              </div>

              {/* 購入実績 — 入荷確認の実績から集計（編集権限者のみ見える） */}
              <PurchaseSummaryCard
                ledger="eng" assetId={detail.id} showHistory={false}
                orderPrefill={{
                  item_id: detail.id, item_source: 'eng',
                  name: detail.name, part_no: detail.sku || detail.asset_no,
                  drawing_no: detail.drawing_no, maker: detail.maker,
                  maker_pn: detail.maker_pn, model_no: detail.model_no,
                  material: detail.material, uom: detail.uom || '個',
                  reference_url: detail.reference_url,
                }}
              />
              {detail.source_development_id && (
                <div className="rounded-lg border border-zinc-200 bg-zinc-50/70 p-3"
                  data-testid="eng-detail-provenance">
                  <p className="text-[11px] text-zinc-400">由来（昇格元の開発案件）</p>
                  <p className="mt-0.5 flex flex-wrap items-center gap-2 text-sm">
                    <a href={`/engineering/developments/${detail.source_development_id}`}
                      className="font-semibold text-teal-700 hover:underline"
                      data-testid="eng-detail-provenance-link">
                      {detail.source_development_title || '開発案件'}
                    </a>
                    {detail.source_development_deleted && (
                      <Badge variant="outline"
                        className="border-rose-300 bg-rose-50 text-[11px] text-rose-700"
                        data-testid="eng-detail-provenance-deleted">削除済み案件</Badge>
                    )}
                    {!detail.source_development_exists && (
                      <Badge variant="outline"
                        className="border-zinc-300 bg-zinc-100 text-[11px] text-zinc-600"
                        data-testid="eng-detail-provenance-missing">案件の記録が見つかりません</Badge>
                    )}
                  </p>
                  <p className="mt-1 text-[11px] text-zinc-500">
                    この資産は開発案件の未登録部品から登録されました。案件が削除されても
                    由来の記録は残ります。
                  </p>
                </div>
              )}
              {detail.photo_url && (
                <StorageLink path={detail.photo_url} target="_blank" rel="noreferrer"
                  className="inline-flex items-center gap-2 text-xs text-teal-700 hover:underline" data-testid="eng-detail-photo">
                  {detail.photo_is_pdf
                    ? <FilePdf size={20} weight="duotone" className="text-rose-600" />
                    : <StorageThumb path={detail.photo_url} alt="" className="w-10 h-10 rounded object-cover border border-zinc-200" />}
                  {detail.photo_filename || '添付ファイル'}
                </StorageLink>
              )}
              {detail.reference_url && (
                <a href={detail.reference_url} target="_blank" rel="noreferrer"
                  className="inline-flex items-center gap-1 text-xs text-teal-600 hover:underline break-all" data-testid="eng-detail-url">
                  <ArrowSquareOut size={13} /> {detail.reference_url}
                </a>
              )}
              {detail.notes && <p className="text-xs text-zinc-500 whitespace-pre-wrap bg-zinc-50 rounded-lg p-3">{detail.notes}</p>}

              {isMember && (
                <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2 pt-1">
                  <div className="flex flex-wrap gap-2">
                    {/* 使用・スクラップ・返却 は状態の帯（ItemLifecycleActions）に1か所だけ置く。
                        ここに同じボタンを並べると同じ操作が二度出る（§8.5）。 */}
                    <Button size="sm" variant="outline"
                      onClick={() => setLabelAsset(detail)}
                      data-testid="eng-detail-qr">
                      <Barcode size={14} className="mr-1" /> ラベル
                    </Button>
                    <Button size="sm" variant="outline"
                      onClick={() => setPriceHistory(detail)}
                      className="border-indigo-200 text-indigo-700 hover:bg-indigo-50" data-testid="eng-detail-price-history">
                      <ClockCounterClockwise size={14} className="mr-1" /> 価格履歴
                    </Button>
                  </div>
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" onClick={() => { setEditing(detail); setFormOpen(true); }} data-testid="eng-detail-edit">
                      <PencilSimple size={14} className="mr-1" /> 編集
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => remove(detail)} className="text-rose-600 hover:bg-rose-50" data-testid="eng-detail-delete">
                      <TrashSimple size={14} />
                    </Button>
                  </div>
                </div>
              )}

              {(detail.movements || []).length > 0 && (
                <div className="pt-2 border-t border-zinc-200">
                  <p className="text-xs font-semibold text-zinc-600 mb-2">履歴</p>
                  <div className="space-y-1.5 max-h-56 overflow-y-auto" data-testid="eng-detail-movements">
                    {[...detail.movements].reverse().map((m) => (
                      <div key={m.id} className="flex items-start gap-2 text-xs bg-zinc-50 rounded-lg px-3 py-2">
                        <span className={`font-bold shrink-0 ${MV_COLOR[m.type] || ''}`}>{MV_LABEL[m.type] || m.type}</span>
                        <span className="tabular-nums shrink-0">×{m.qty}</span>
                        <span className="text-zinc-600 flex-1 min-w-0">
                          {m.project_name && <span className="font-medium">{m.project_name} </span>}
                          {m.tehai_no && (
                            <span className="font-mono text-[11px] text-teal-700 mr-1"
                              data-testid={`eng-mv-tehai-${m.id}`}>{m.tehai_no}</span>
                          )}
                          {m.purpose_linked_label && (
                            <span className="text-[11px] text-teal-700 mr-1">{m.purpose_linked_label}</span>
                          )}
                          {m.purpose_reason_label && (
                            <span className="text-[11px] text-zinc-500 mr-1">{m.purpose_reason_label}</span>
                          )}
                          {m.purpose || m.reason || m.note || ''}
                        </span>
                        <span className="text-zinc-400 shrink-0">{(m.at || '').slice(0, 10)} {m.by}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="pt-2 border-t border-zinc-200">
                <LinkedDocsPanel targetType="eng_asset" targetId={detail.id} />
              </div>

              <div className="pt-2 border-t border-zinc-200">
                <TraceabilityPanel entityType="eng_asset" entityId={detail.id} />
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      <EngAssetFormDialog open={formOpen} onClose={() => setFormOpen(false)} editing={editing}
        onSaved={(fresh) => { editing ? onChanged(fresh) : load(); }} />
      <AiAssetImportModal module="eng" open={aiImportOpen} onClose={() => setAiImportOpen(false)} onImported={load} />
      <EngAssetActionDialog open={!!action} onClose={() => setAction(null)}
        asset={action?.asset} mode={action?.mode} onDone={onChanged} />
      <PriceHistoryDialog asset={priceHistory} onClose={() => setPriceHistory(null)} />
      {labelBatch && (
        <LabelPrintDialog open onClose={() => setLabelBatch(null)} title="技術資産のラベル（一括）"
          labels={labelBatch} />
      )}
      {labelAsset && (
        <LabelPrintDialog open onClose={() => setLabelAsset(null)} title="技術資産のラベル"
          labels={[buildEngAssetLabel(labelAsset)]} />
      )}
    </div>
  );
}

// Column renderer for 単価 — shows latest, with initial + drift when they differ.
function renderUnitPrice(r) {
  const initial = r.unit_price;
  const last = r.last_purchase_price;
  const hasInitial = initial != null && Number(initial) > 0;
  const hasLast = last != null && Number(last) > 0;
  if (!hasInitial && !hasLast) return <span className="text-zinc-300">—</span>;
  const drift = (hasInitial && hasLast && Number(initial) > 0)
    ? (Number(last) - Number(initial)) / Number(initial) : 0;
  const showDrift = hasInitial && hasLast && Math.abs(drift) >= 0.1;
  return (
    <div className="flex flex-col leading-tight">
      <span className="tabular-nums text-zinc-800">
        {hasLast ? `¥${Number(last).toLocaleString()}` : `¥${Number(initial).toLocaleString()}`}
      </span>
      {hasInitial && hasLast && Number(initial) !== Number(last) && (
        <span className="text-[10px] text-zinc-400 tabular-nums">
          初期 ¥{Number(initial).toLocaleString()}
          {showDrift && (
            <span className={`ml-1 font-semibold ${drift > 0 ? 'text-rose-600' : 'text-emerald-600'}`}>
              {drift > 0 ? <TrendUp size={9} weight="bold" className="inline" /> : <TrendDown size={9} weight="bold" className="inline" />}
              {drift > 0 ? '+' : ''}{(drift * 100).toFixed(0)}%
            </span>
          )}
        </span>
      )}
    </div>
  );
}

function PriceHistoryDialog({ asset, onClose }) {
  const [rows, setRows] = useState(null);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState('');

  const attachInvoice = async (row, file) => {
    if (!file) return;
    setUploading(row.id);
    try {
      const fd = new FormData();
      fd.append('file', file);
      await engApi.attachPurchaseInvoice(row.id, fd);
      toast.success('請求書を添付しました（ドキュメントにも入ります）');
      const r = await engApi.assetPriceHistory(asset.id);
      setRows(r.rows || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || '添付できませんでした');
    } finally { setUploading(''); }
  };

  useEffect(() => {
    if (!asset) return;
    setLoading(true); setRows(null);
    engApi.assetPriceHistory(asset.id)
      .then((r) => setRows(r.rows || []))
      .catch(() => { setRows([]); toast.error('価格履歴の取得に失敗しました'); })
      .finally(() => setLoading(false));
  }, [asset]);
  return (
    <Dialog open={!!asset} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="eng-price-history-dialog">
        {asset && (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <ClockCounterClockwise size={18} weight="duotone" className="text-indigo-600" />
                価格履歴
                <span className="font-mono text-xs text-zinc-500">{asset.sku || asset.asset_no}</span>
                <span className="text-sm font-normal text-zinc-600">{asset.name}</span>
              </DialogTitle>
            </DialogHeader>
            {loading ? (
              <div className="py-10 text-center text-sm text-zinc-400">読み込み中...</div>
            ) : (rows && rows.length > 0) ? (
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead className="bg-zinc-100 text-zinc-700 text-[10px] font-semibold uppercase">
                    <tr>
                      <th className="text-left px-2 py-1.5">購入日</th>
                      <th className="text-left px-2 py-1.5">仕入先</th>
                      <th className="text-right px-2 py-1.5">数量</th>
                      <th className="text-right px-2 py-1.5">単価</th>
                      <th className="text-right px-2 py-1.5">合計</th>
                      <th className="text-left px-2 py-1.5">発注伝票No</th>
                      <th className="text-left px-2 py-1.5">データ元</th>
                      <th className="text-left px-2 py-1.5">請求書</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-zinc-200">
                    {rows.map((r) => (
                      <tr key={r.id} className="hover:bg-teal-50/50" data-testid={`price-history-row-${r.id}`}>
                        <td className="px-2 py-1.5 whitespace-nowrap tabular-nums">{r.received_date || '—'}</td>
                        <td className="px-2 py-1.5">{r.supplier || <span className="text-zinc-400">—</span>}</td>
                        <td className="px-2 py-1.5 text-right tabular-nums">{r.qty}</td>
                        <td className="px-2 py-1.5 text-right tabular-nums">{r.unit_price != null ? `¥${Number(r.unit_price).toLocaleString()}` : '—'}</td>
                        <td className="px-2 py-1.5 text-right tabular-nums font-semibold">¥{Number(r.actual_price || 0).toLocaleString()}</td>
                        <td className="px-2 py-1.5 font-mono text-[10px] text-zinc-500">{r.request_no || '—'}</td>
                        <td className="px-2 py-1.5">{renderSourceBadge(r.source)}</td>
                        {/* 3 · 入荷確認が済んだ行に 請求書／領収書 を付ける（2026-11-05） */}
                        <td className="px-2 py-1.5">
                          {r.invoice_document_id ? (
                            <a href={`/docs?open=${r.invoice_document_id}`} target="_blank" rel="noreferrer"
                              className="text-[11px] font-semibold text-teal-700 underline"
                              data-testid={`price-history-invoice-${r.id}`}>
                              添付あり
                            </a>
                          ) : r.received_date && !r.voided_at ? (
                            <label className="cursor-pointer text-[11px] font-semibold text-zinc-600 underline"
                              data-testid={`price-history-invoice-add-${r.id}`}>
                              請求書を添付
                              <input type="file" className="hidden"
                                accept="application/pdf,image/*"
                                disabled={uploading === r.id}
                                onChange={(e) => attachInvoice(r, e.target.files?.[0])} />
                            </label>
                          ) : <span className="text-zinc-400">—</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="py-10 text-center text-sm text-zinc-400">履歴がありません</div>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

const SOURCE_LABEL = {
  arrival_confirm: '入荷確認',
  arrival_repair: '¥1バグ復旧',
  receive_correction: '数量修正',
};
const SOURCE_STYLE = {
  arrival_confirm: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  arrival_repair: 'bg-amber-50 text-amber-700 border-amber-200',
  receive_correction: 'bg-sky-50 text-sky-700 border-sky-200',
};
function renderSourceBadge(source) {
  if (!source) return <span className="text-zinc-400">—</span>;
  const label = SOURCE_LABEL[source] || source;
  const style = SOURCE_STYLE[source] || 'bg-zinc-50 text-zinc-600 border-zinc-200';
  return <span className={`px-1.5 py-0.5 rounded border text-[10px] font-semibold ${style}`}>{label}</span>;
}

function Field({ label, value, mono }) {
  return (
    <div>
      <p className="text-[11px] text-zinc-400">{label}</p>
      <p className={`text-zinc-800 ${mono ? 'font-mono' : ''}`}>{value || <span className="text-zinc-300">—</span>}</p>
    </div>
  );
}
